/**
 * F8 · Room screen (screens §F8): the projector. One 1920×1080 presenter-dark stage scaled to the window; it shows whatever the facilitator
 * projects (`roomScreenGet`, refreshed by the shell on `room.screen_changed` and `finale.stage_changed`) and, except in the finale,
 * the value-free pipeline strip. Never a supervisory rating, a value on a dot, or a rank before finale stage `rank`.
 */
import { useEffect, useState } from "react";
import type { RoomScreenView } from "@bsim/contracts";
import type { ApiClient } from "../api/client";
import { useRoute } from "../api/context";
import { PIPELINE_WORDS, STATE_WORDS } from "../facilitator/derive";
import { roundDone } from "../facilitator/words";
import { formatCountdown } from "../shell/RoundClock";
import { useNow } from "../shell/useNow";
import { Finale } from "./Finale";
import { Pick, Slide, Surprise } from "./Content";
import "./room.css";

const STAGE_W = 1920;
const STAGE_H = 1080;

/** Below this window width (a phone or a workshop tablet) the stage is no longer shrunk to fit: it keeps a readable scale and reflows to the window. */
const FLUID_BELOW = 1100;
/** The scale a fluid stage keeps, from 0.6 on a phone (28 px body type reads as about 17 px) to 0.85 at a tablet's width, so a workshop tablet reads it across a table. */
const fluidScale = (width: number): number => 0.6 + 0.25 * Math.min(1, Math.max(0, (width - 400) / (FLUID_BELOW - 400)));

interface StageFit { readonly scale: number; readonly fluid: boolean; readonly w: number; readonly h: number }

/**
 * The stage's fit. From a laptop to a projector the fixed 1920×1080 stage is scaled into the window, so type stays proportional.
 * On a narrow window that would draw 28 px type at 5 to 11 px, so the stage keeps a readable scale and takes the window's shape instead (portrait included).
 */
function useFitScale(): StageFit {
  const fit = (): StageFit => {
    const w = window.innerWidth, h = window.innerHeight;
    const f = fluidScale(w);
    return w < FLUID_BELOW ? { scale: f, fluid: true, w: w / f, h: h / f } : { scale: Math.min(w / STAGE_W, h / STAGE_H), fluid: false, w: STAGE_W, h: STAGE_H };
  };
  const [stage, setStage] = useState(fit);
  useEffect(() => {
    const onResize = () => setStage(fit());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return stage;
}

/** What the room's headline says when there is no countdown to show: where the round stands, in plain words (OR-30.42). */
export function stateHeadline(clock: RoomScreenView["clock"]): string {
  const n = clock.roundNo;
  if (n === null || clock.state === null) return "The first round opens soon";
  switch (clock.state) {
    case "draft": return `Round ${n} opens soon`;
    case "open": return `Round ${n} is open`;
    case "locked": return `Round ${n} is closed`;
    case "resolving": case "resolvingPhase2": return `Round ${n} is running the year`;
    case "awaitingSurprise": return `Round ${n} is paused for a surprise`;
    case "resolved": case "released": return `Round ${n} results are out`;
    case "paused": return `Round ${n} is paused`;
  }
}

function Clock({ clock }: { readonly clock: RoomScreenView["clock"] }) {
  const now = useNow();
  const ms = clock.countdownEndsAt === null ? null : Date.parse(clock.countdownEndsAt) - now;
  const eyebrow = clock.roundNo === null ? "Waiting for the round" : `Round ${clock.roundNo}`;
  // A running countdown is the screen; with none (between rounds, paused) a headline says where the round stands and the pipeline sits under it.
  if (ms !== null && !clock.paused) {
    return (
      <section className="gx-room-main" data-testid="room.clock">
        <div className="gx-room-eyebrow">{eyebrow}{clock.state === null ? "" : ` · ${STATE_WORDS[clock.state]}`}</div>
        <div className="gx-room-clock gx-num" role="timer">{formatCountdown(ms)}</div>
      </section>
    );
  }
  return (
    <section className="gx-room-main gx-room-main--state" data-testid="room.clock">
      <div className="gx-room-eyebrow">Governexus Bank Simulator</div>
      <h1 className="gx-room-headline gx-room-headline--state">{stateHeadline(clock)}</h1>
    </section>
  );
}

function Pipeline({ pipeline, clock }: { readonly pipeline: RoomScreenView["pipeline"]; readonly clock: RoomScreenView["clock"] }) {
  // Between rounds every team has finished the round just played: the room says so, not "Not started".
  const done = clock.roundNo !== null && clock.state !== null && roundDone({ state: clock.state }) ? `Round ${clock.roundNo} complete` : null;
  return (
    <ul className="gx-room-pipe" aria-label="Where each team is">
      {pipeline.map((p) => (
        <li key={p.teamId} data-stage={p.stage}>
          <i aria-hidden="true" />
          <span className="gx-room-pipe-name">{p.name}</span>
          <span className="gx-room-pipe-stage">{done ?? PIPELINE_WORDS[p.stage]}</span>
        </li>
      ))}
    </ul>
  );
}

function Body({ view }: { readonly view: RoomScreenView }) {
  const c = view.content;
  switch (c.kind) {
    case "pick": return <Pick pick={c.pick} />;
    case "slide": return <Slide slide={c.slide} cohortId={view.cohortId} />;
    case "surprise": return <Surprise card={c.card} />;
    case "finale": return <Finale cohortId={view.cohortId} finale={c.finale} />;
    case "none": return <Clock clock={view.clock} />;
  }
}

export function RoomScreenPage({ cohortId }: { readonly cohortId: string; readonly api: ApiClient }) {
  const room = useRoute("roomScreenGet", { cohortId });
  const { scale, fluid, w, h } = useFitScale();
  const view = room.data;
  return (
    <main className="gx-room" data-surface="projector" data-testid="room.screen" data-content={view?.content.kind ?? "loading"} data-fluid={fluid || undefined}>
      <div className="gx-room-stage" style={fluid ? { transform: `scale(${scale})`, width: w, minHeight: h, ["--room-w" as string]: `${w}px` } : { transform: `scale(${scale})` }}>
        {view === undefined ? <section className="gx-room-main"><div className="gx-room-eyebrow">Governexus Bank Simulator</div></section> : <Body view={view} />}
        {view?.joinCode === undefined ? null : <div className="gx-room-join" data-testid="room.join">Join at <b>{`${globalThis.location?.origin ?? ""}/join`}</b> with the code <b className="gx-num" data-testid="room.join.code">{view.joinCode}</b></div>}
        {view === undefined || view.content.kind === "finale" ? null : <Pipeline pipeline={view.pipeline} clock={view.clock} />}
      </div>
    </main>
  );
}
